import React, { useState, useRef, useEffect } from "react";
import { motion, useScroll, useTransform, useMotionValue, useMotionValueEvent } from "framer-motion";
import { LuArrowUp, LuStar, LuQuote } from "react-icons/lu";

export default function Testimonial({ className }) {
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  const interpolatedProgress = useMotionValue(0);
  const targetProgress = useRef(0);
  const currentProgress = useRef(0);
  const isSeeking = useRef(false);
  const pendingTime = useRef(null);

  useMotionValueEvent(scrollYProgress, "change", (latest) => {
    targetProgress.current = latest;
  });

  const panelAX = useTransform(interpolatedProgress, [0, 0.42, 0.52, 1], ["0px", "0px", "-160%", "-160%"]);
  const panelAOpacity = useTransform(interpolatedProgress, [0, 0.42, 0.52, 1], [1, 1, 0, 0]);

  const panelBX = useTransform(interpolatedProgress, [0, 0.42, 0.52, 1], ["160%", "160%", "0px", "0px"]);
  const panelBOpacity = useTransform(interpolatedProgress, [0, 0.42, 0.52, 1], [0, 0, 1, 1]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    video.pause();
    video.preload = "auto";

    let rafId = 0;

    const updateFrame = () => {
      const target = targetProgress.current;
      const current = currentProgress.current;
      const diff = target - current;

      if (Math.abs(diff) > 0.0001) {
        currentProgress.current += diff * 0.08;
      } else if (current !== target) {
        currentProgress.current = target;
      }

      interpolatedProgress.set(currentProgress.current);

      if (video.duration && !isNaN(video.duration)) {
        const targetTime = currentProgress.current * video.duration;
        const boundedTime = Math.max(0, Math.min(targetTime, video.duration - 0.15));

        if (!isSeeking.current) {
          isSeeking.current = true;
          video.currentTime = boundedTime;
        } else {
          pendingTime.current = boundedTime;
        }
      }

      rafId = requestAnimationFrame(updateFrame);
    };

    const handleSeeked = () => {
      isSeeking.current = false;
      if (pendingTime.current !== null) {
        const nextTime = pendingTime.current;
        pendingTime.current = null;
        isSeeking.current = true;
        video.currentTime = nextTime;
      }
    };

    video.addEventListener("seeked", handleSeeked);

    const syncInitialFrame = () => {
      if (video.duration) {
        const initialTime = targetProgress.current * video.duration;
        video.currentTime = Math.max(0, Math.min(initialTime, video.duration - 0.15));
      }
    };
    video.addEventListener("loadedmetadata", syncInitialFrame);

    rafId = requestAnimationFrame(updateFrame);

    return () => {
      cancelAnimationFrame(rafId);
      video.removeEventListener("seeked", handleSeeked);
      video.removeEventListener("loadedmetadata", syncInitialFrame);
    };
  }, [interpolatedProgress]);

  return (
    <>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap" rel="stylesheet" />

      <div
        ref={containerRef}
        className={"relative w-full h-[320vh] bg-[#16120e] " + (className || "")}
        id="explore-container"
      >
        <section
          id="explore-section"
          className="sticky top-0 w-full h-screen flex flex-col md:flex-row overflow-hidden border-t border-[#2B170A]/10 bg-[#16120e]"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {/* Immersive Video Background */}
          <div className="absolute inset-0 w-full h-full z-0 overflow-hidden">
            <video
              ref={videoRef}
              src="https://cdn.jiro.build/Laounge%20Coffe/Videos/Night%20Mood%20Chnage%20Coffe.mp4"
              playsInline
              muted
              preload="auto"
              className="w-full h-full object-cover object-center transition-transform duration-[2000ms] ease-out"
              style={{
                transform: isHovered ? "scale(1.04) translateY(-5px)" : "scale(1) translateY(0px)",
                filter: "brightness(0.9) contrast(1.02)",
              }}
            />
            <div className="absolute inset-0 bg-black/25 pointer-events-none" />
          </div>

          {/* Left Glass Panel (Content Area) */}
          <div
            id="explore-glass-panel"
            className="relative z-10 w-full md:w-[460px] lg:w-[520px] h-full shrink-0 flex flex-col justify-between p-8 sm:p-12 lg:p-16 backdrop-blur-[32px] bg-black/20 border-r border-white/10 text-white select-none shadow-2xl shadow-black/45 overflow-hidden"
          >
            {/* Top Header & Scroll Indicator */}
            <div className="flex items-center justify-between z-20">
              <div
                className="w-12 h-12 rounded-full border border-white/20 flex items-center justify-center text-white/90 hover:bg-white/10 transition-all duration-500 cursor-pointer group shadow-lg shadow-black/10"
                style={{
                  transform: isHovered ? "translateY(-4px)" : "translateY(0px)",
                }}
              >
                <LuArrowUp className="w-5 h-5 transition-transform duration-500 group-hover:translate-y-[-2px]" strokeWidth={2.2} />
              </div>

              <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/5 font-mono text-[10px] tracking-wider text-white/60">
                <span className="w-1.5 h-1.5 rounded-full bg-[#FF7A00] animate-pulse" />
                <span>Scroll to Explore</span>
              </div>
            </div>

            {/* Dynamic Sliding Content Area */}
            <div className="relative flex-1 flex flex-col justify-center min-h-[300px]">
              {/* Panel A (Feature Description) */}
              <motion.div
                style={{
                  x: panelAX,
                  opacity: panelAOpacity,
                  position: "absolute",
                  width: "100%",
                  left: 0,
                  right: 0,
                }}
                className="flex flex-col gap-6 md:gap-8"
              >
                <div className="flex flex-col gap-2">
                  <span
                    className="text-[11px] sm:text-xs font-semibold tracking-[0.2em] text-[#E7D7C1] uppercase"
                    style={{ letterSpacing: "0.22em" }}
                  >
                    Rise Above Mediocrity
                  </span>
                  <p className="text-sm sm:text-[15px] text-white/70 leading-relaxed font-normal">
                    With a precision-engineered lift (exactly one coaster thick), Lounge Coffe doesn't just hold your mug - it elevates it. Literally. Above every boring surface you've ever known.
                  </p>
                </div>

                <div className="w-full border-t border-dashed border-white/20 my-1" />

                <h2
                  className="font-extrabold uppercase text-white tracking-tight leading-[1.05]"
                  style={{
                    fontSize: "clamp(32px, 4.5vw, 52px)",
                    fontWeight: 800,
                  }}
                >
                  Elevate Your <br /> Coffee <br /> Experience
                </h2>
              </motion.div>

              {/* Panel B (Premium Testimonial) */}
              <motion.div
                style={{
                  x: panelBX,
                  opacity: panelBOpacity,
                  position: "absolute",
                  width: "100%",
                  left: 0,
                  right: 0,
                }}
                className="flex flex-col gap-5 sm:gap-7"
              >
                <div className="flex flex-col gap-2.5">
                  <div className="flex items-center gap-1">
                    {[0, 1, 2, 3, 4].map((idx) => (
                      <LuStar key={idx} className="w-4 h-4 fill-[#FF7A00] text-[#FF7A00]" />
                    ))}
                  </div>
                  <span className="text-[11px] font-mono tracking-widest text-[#FF7A00] uppercase font-bold">
                    Verified Collector Review
                  </span>
                </div>

                <div className="relative">
                  <LuQuote className="w-10 h-10 text-white/10 absolute -top-4 -left-3" />
                  <p className="text-base sm:text-lg md:text-xl font-medium italic text-white/90 leading-relaxed pl-5">
                    "The ultimate minimalist coffee companion. The literal lift keeps my workspace clean, my mug floating, and my focus sharp during high-intensity creative coding sessions."
                  </p>
                </div>

                <div className="w-full border-t border-dashed border-white/20 my-1" />

                <div className="flex flex-col">
                  <span className="font-bold text-[#E7D7C1] text-base">Marcus Vance</span>
                  <span className="text-xs text-white/50 font-mono uppercase tracking-wider mt-0.5">Design Director, San Francisco</span>
                </div>
              </motion.div>
            </div>

            {/* Bottom Footer Info */}
            <div className="flex items-center gap-2 text-white/30 text-sm font-mono mt-8 md:mt-0 z-20">
              <span className="w-2 h-2 rounded-full bg-[#E7D7C1]/40 animate-pulse" />
              <span className="tracking-widest text-[10px] uppercase">Lounge Coffe Co.</span>
            </div>
          </div>

          {/* Right Floating Typography (Desktop Only) */}
          <div
            className="absolute bottom-8 right-8 sm:bottom-12 sm:right-12 text-right z-10 hidden sm:flex flex-col items-end gap-1 select-none text-white pointer-events-none drop-shadow-md"
            style={{
              transform: isHovered ? "translateY(-2px)" : "translateY(0px)",
              transition: "transform 1.5s cubic-bezier(0.16, 1, 0.3, 1)",
            }}
          >
            <span className="text-[10px] sm:text-xs tracking-[0.18em] uppercase text-white/80 font-medium">
              Constant lift via geometry
            </span>
            <span
              className="text-4xl sm:text-5xl lg:text-[64px] font-light text-white mt-1 tracking-wider flex items-center"
              style={{
                fontFamily: "Georgia, serif",
                opacity: 0.95,
              }}
            >
              Δh ≈ t
            </span>
          </div>
        </section>
      </div>
    </>
  );
}